import { Checkbox, Collapse, Form, InputNumber, message, Spin, Tooltip, Typography } from 'antd';
import { Banner } from 'components/ui/Banner';
import { useCallback, useEffect, useState } from 'react';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { useAsync } from 'react-async-hook';
import PageMeta from 'components/PageMeta/PageMeta';
import RadioCards from 'components/RadioCards/RadioCards';
import { FAMILY_BRANCH_MAP } from '../../familyApplicationConstants';
import { Information } from '@carbon/icons-react';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getApplicantFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

// Accordions
const SECTIONS = {
  ENLISTED: 'ENLISTED',
  WARRANT_OFFICER: 'WARRANT_OFFICER',
  OFFICER: 'OFFICER',
};
const collapseItemStyles = {
  body: {
    padding: 0,
  },
};

/**
 * Reusable component for rank option labels
 */
const RankLabel = ({ option, isCompletedBySponsor }) => (
  <>
    <span>{option.name}</span>
    <span className="font-normal">{option.payGrade && ` (${option.payGrade})`}</span>
    {!isCompletedBySponsor && option.description && <span className="font-normal">{` - ${option.description}`}</span>}
  </>
);

export function FamilyApplicationRank({ application, setApplication, household, loading, setStep, readOnly = false }) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [submitting, setSubmitting] = useState(false);

  const isCompletedBySponsor = application?.completedBySponsor === true;
  const programSponsorId = application?.applicant?.militaryDetails?.branch?.id;

  // Form state
  const [form] = Form.useForm();

  // API fetches
  const { result: enlistedOptions = null } = useAsync(async () => {
    if (programSponsorId === undefined) {
      return null;
    }

    const data = await familyService.getOptions(
      `/ref-data/military-info/ranks?branchId=${programSponsorId}&type=${SECTIONS.ENLISTED}`,
      {
        cache: { interpretHeader: false },
      },
    );

    return processRankData(data);
  }, [programSponsorId]);

  const { result: warrantOfficerOptions = null } = useAsync(async () => {
    if (programSponsorId === undefined) {
      return null;
    }

    const data = await familyService.getOptions(
      `/ref-data/military-info/ranks?branchId=${programSponsorId}&type=${SECTIONS.WARRANT_OFFICER}`,
      {
        cache: { interpretHeader: false },
      },
    );

    return processRankData(data);
  }, [programSponsorId]);

  const { result: officerOptions = null } = useAsync(async () => {
    if (programSponsorId === undefined) {
      return null;
    }

    const data = await familyService.getOptions(
      `/ref-data/military-info/ranks?branchId=${programSponsorId}&type=${SECTIONS.OFFICER}`,
      {
        cache: { interpretHeader: false },
      },
    );

    return processRankData(data);
  }, [programSponsorId]);

  // Accordions
  const [activeSection, setActiveSection] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [gradeUnknown, setGradeUnknown] = useState(false);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.RANK, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        // FAMILY-APPS__DATA_CLEANING - Check if grade changed
        const currentGradeId = application?.applicant?.militaryDetails?.grade?.id;
        const newGradeId = gradeUnknown ? null : selectedOption;
        const gradeChanged = currentGradeId !== newGradeId;

        const updatedMilitaryDetails = {
          ...application?.applicant?.militaryDetails,
          grade: gradeUnknown
            ? null
            : {
                id: selectedOption,
              },
          gradeUnknown,
          yearsOfService: values?.yearsOfService ?? null,
        };

        const _newSponsorData = await familyService.addUpdateAdult(household?.id, application?.applicant?.id ?? null, {
          ...application?.applicant,
          militaryDetails: updatedMilitaryDetails,
        });

        const _newApplication = await familyService.saveApplication({
          ...application,
          applicant: {
            ...application?.applicant,
            ..._newSponsorData,
          },
          // FAMILY-APPS__DATA_CLEANING - Clear eligibility certification if sponsor's grade changes
          ...(gradeChanged && {
            selfCertCompletedBySponsor: null,
            selfcertAccepted: null,
            selfcertAcceptedBy: null,
          }),
        });

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, selectedOption, gradeUnknown, setApplication, setStep, stepConfig],
  );

  // Set the selected option based on the application data
  useEffect(() => {
    // Set gradeUnknown state
    if (application?.applicant?.militaryDetails?.gradeUnknown !== undefined) {
      setGradeUnknown(application?.applicant?.militaryDetails?.gradeUnknown);
    }

    if (
      application?.applicant?.militaryDetails?.grade?.id !== undefined &&
      enlistedOptions !== null &&
      warrantOfficerOptions !== null &&
      officerOptions !== null
    ) {
      let _activeSection = null;
      if (enlistedOptions?.some((option) => option.id === application?.applicant?.militaryDetails?.grade?.id)) {
        _activeSection = SECTIONS.ENLISTED;
      } else if (
        warrantOfficerOptions?.some((option) => option.id === application?.applicant?.militaryDetails?.grade?.id)
      ) {
        _activeSection = SECTIONS.WARRANT_OFFICER;
      } else if (officerOptions?.some((option) => option.id === application?.applicant?.militaryDetails?.grade?.id)) {
        _activeSection = SECTIONS.OFFICER;
      }

      setActiveSection(_activeSection);
      setSelectedOption(application?.applicant?.militaryDetails?.grade?.id);
    }

    // Set the years of service
    form.setFieldsValue({
      yearsOfService: application?.applicant?.militaryDetails?.yearsOfService ?? null,
    });
  }, [
    application?.applicant?.militaryDetails?.grade?.id,
    application?.applicant?.militaryDetails?.gradeUnknown,
    application?.applicant?.militaryDetails?.yearsOfService,
    enlistedOptions,
    form,
    officerOptions,
    warrantOfficerOptions,
  ]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={enlistedOptions === null || warrantOfficerOptions === null || officerOptions === null}>
      <PageMeta title="Rank" />

      <div className="px-7">
        <Banner
          question={
            isCompletedBySponsor
              ? "What's your current rank?"
              : `What's ${getApplicantFirstName(application?.applicant)}'s current rank?`
          }
          guidance={isCompletedBySponsor ? undefined : "Check their LES under 'Grade'"}
          className="my-8"
        />
      </div>

      <Form form={form} onFinish={onSubmit} layout="vertical">
        <div className="px-7 pt-4">
          <Form.Item
            name="rank"
            rules={[
              {
                validator: (_, value) => {
                  if (!selectedOption && !gradeUnknown) {
                    return Promise.reject(new Error('Please select a rank, or check the "I don\'t know" box'));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Collapse
              data-testid="rank-collapse"
              accordion
              expandIconPosition="end"
              items={[
                {
                  key: SECTIONS.ENLISTED,
                  label: (
                    <Tooltip title="Enlisted soldiers are the backbone of the Army. They handle specific jobs and make up most of the military personnel.">
                      <div className="inline-flex gap-2 items-center">
                        Enlisted <Information className="mb-[-1px]" />
                      </div>
                    </Tooltip>
                  ),
                  children: (
                    <RadioCards
                      variant="inline"
                      options={enlistedOptions?.map((_option) => ({
                        label: <RankLabel option={_option} isCompletedBySponsor={isCompletedBySponsor} />,
                        value: _option.id,
                      }))}
                      value={selectedOption}
                      onChange={(value) => {
                        setSelectedOption(value);
                        setGradeUnknown(false);
                      }}
                      disabled={readOnly}
                    />
                  ),
                  styles: collapseItemStyles,
                },
                ...(![FAMILY_BRANCH_MAP.airForce.id, FAMILY_BRANCH_MAP.spaceForce.id].includes(programSponsorId)
                  ? [
                      {
                        key: SECTIONS.WARRANT_OFFICER,
                        label: (
                          <Tooltip title="Warrant Officers are experts in a specific technical field. They rank between enlisted soldiers and officers.">
                            <div className="inline-flex gap-2 items-center">
                              Warrant Officers <Information className="mb-[-1px]" />
                            </div>
                          </Tooltip>
                        ),
                        children: (
                          <RadioCards
                            variant="inline"
                            options={warrantOfficerOptions?.map((_option) => ({
                              label: <RankLabel option={_option} isCompletedBySponsor={isCompletedBySponsor} />,
                              value: _option.id,
                            }))}
                            value={selectedOption}
                            onChange={(value) => {
                              setSelectedOption(value);
                              setGradeUnknown(false);
                            }}
                            disabled={readOnly}
                          />
                        ),
                        styles: collapseItemStyles,
                      },
                    ]
                  : []),
                {
                  key: SECTIONS.OFFICER,
                  label: (
                    <Tooltip title="Officers lead Army units and oversee operations. They have college degrees and outrank enlisted personnel.">
                      <div className="inline-flex gap-2 items-center">
                        Officers <Information className="mb-[-1px]" />
                      </div>
                    </Tooltip>
                  ),
                  children: (
                    <RadioCards
                      variant="inline"
                      options={officerOptions?.map((_option) => ({
                        label: <RankLabel option={_option} isCompletedBySponsor={isCompletedBySponsor} />,
                        value: _option.id,
                      }))}
                      value={selectedOption}
                      onChange={(value) => {
                        setSelectedOption(value);
                        setGradeUnknown(false);
                      }}
                      disabled={readOnly}
                    />
                  ),
                  styles: collapseItemStyles,
                },
              ]}
              activeKey={activeSection}
              onChange={(_activeKey) => {
                setActiveSection(_activeKey);
              }}
            />

            <Checkbox
              data-testid="grade-unknown-checkbox"
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedOption(null);
                  setActiveSection(null);
                }

                setGradeUnknown(e.target.checked);
              }}
              checked={gradeUnknown}
              disabled={readOnly}
              className="mt-10"
            >
              <Typography.Text className="text-[15px] font-semibold">I don't know right now</Typography.Text>
            </Checkbox>
          </Form.Item>

          <Form.Item name="yearsOfService" label="How many years in service? (optional)" className="mt-12 mb-8">
            <InputNumber
              data-testid="years-of-service-input"
              size="large"
              addonAfter="Year(s)"
              min={0}
              controls={false}
              disabled={readOnly}
              className="w-48"
            />
          </Form.Item>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(FAMILY_APPLICATION_STEPS.RANK, application, stepConfig);

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}

/**
 * Processes rank data by removing the payGrade from the name field
 * @param {Array} ranks - Array of rank objects
 * @returns {Array} - Processed ranks with cleaned names
 */
const processRankData = (ranks) => {
  if (!ranks || !Array.isArray(ranks)) {
    return ranks;
  }

  return ranks.map((rank) => {
    if (!rank.name || typeof rank.name !== 'string' || !rank.payGrade) {
      return rank;
    }

    // Create regex to match the specific payGrade in brackets with optional spaces
    // Escape special regex characters in the payGrade value
    const escapedPayGrade = rank.payGrade.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const payGradeRegex = new RegExp(`\\s*\\(${escapedPayGrade}\\)\\s*$`);
    const cleanedName = rank.name.replace(payGradeRegex, '').trim();

    return {
      ...rank,
      name: cleanedName,
    };
  });
};
